import { useCallback, useEffect, useRef, useState } from 'react';

import { Toast } from 'js/src/components/common/Toast';
import {
    ALL_FORMATS,
    AudioSample,
    BlobSource,
    Conversion,
    Input,
    Mp4OutputFormat,
    Output,
    StreamTarget,
} from 'mediabunny';

export interface AudioTrack {
    id: string;
    file: File;
    name: string;
    offsetMs: number;
    durationMs: number;
    volume: number; // 0-100
}

export interface MixerState {
    videoFile: File | null;
    videoDurationMs: number;
    videoPreviewUrl: string | null;
    audioTracks: AudioTrack[];
    videoVolume: number; // 0-100
    isProcessing: boolean;
    previewUrl: string | null;
}

interface UseMediaBunnyMixerReturn {
    state: MixerState;
    setVideoFile: (file: File | null) => void;
    addAudioTrack: (file: File) => Promise<void>;
    removeAudioTrack: (id: string) => void;
    updateAudioTrackOffset: (id: string, offsetMs: number) => void;
    updateAudioTrackVolume: (id: string, volume: number) => void;
    setVideoVolume: (volume: number) => void;
    generatePreview: () => Promise<void>;
    downloadVideo: () => Promise<void>;
    reset: () => void;
}

const generateId = () => Math.random().toString(36).substring(7);

/**
 * Gets audio duration using AudioContext
 */
async function getAudioDuration(file: File): Promise<number> {
    const audioContext = new AudioContext();
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const durationMs = audioBuffer.duration * 1000;
    await audioContext.close();
    return durationMs;
}

/**
 * Gets video duration using HTML video element
 */
async function getVideoDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
            URL.revokeObjectURL(video.src);
            resolve(video.duration * 1000);
        };
        video.onerror = () => {
            URL.revokeObjectURL(video.src);
            reject(new Error('Failed to load video metadata'));
        };
        video.src = URL.createObjectURL(file);
    });
}

/**
 * Creates a mixed AudioBuffer from multiple audio tracks with offsets and volumes.
 * Note: Video audio is NOT included here - it's handled in the mediabunny
 * process callback to avoid loading the entire video into memory.
 */
async function createMixedAudioBuffer(
    audioTracks: AudioTrack[],
    targetDurationMs: number,
): Promise<AudioBuffer> {
    const sampleRate = 44100;
    const numberOfChannels = 2;
    const totalSamples = Math.ceil((targetDurationMs / 1000) * sampleRate);

    const audioContext = new AudioContext({ sampleRate });
    const mixedBuffer = audioContext.createBuffer(numberOfChannels, totalSamples, sampleRate);

    // Decode all audio tracks
    for (const track of audioTracks) {
        const arrayBuffer = await track.file.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const offsetSamples = Math.floor((track.offsetMs / 1000) * sampleRate);
        // Convert volume percentage (0-100) to a multiplier (0-1)
        const volumeMultiplier = track.volume / 100;

        for (let channel = 0; channel < numberOfChannels; channel++) {
            const outputData = mixedBuffer.getChannelData(channel);
            const sourceChannel = channel < audioBuffer.numberOfChannels ? channel : 0;
            const sourceData = audioBuffer.getChannelData(sourceChannel);

            for (let i = 0; i < sourceData.length; i++) {
                const targetIndex = offsetSamples + i;
                if (targetIndex >= 0 && targetIndex < totalSamples) {
                    outputData[targetIndex] += sourceData[i] * volumeMultiplier;
                }
            }
        }
    }

    await audioContext.close();
    return mixedBuffer;
}

/**
 * Mixes video with audio tracks using mediabunny
 * Uses the audio process callback to replace the original audio with our mixed audio
 */
async function mixVideoWithAudio(
    videoFile: File,
    audioTracks: AudioTrack[],
    videoVolume: number,
    videoDurationMs: number,
) {
    // Create mixed buffer from audio tracks only (video audio handled in process callback)
    const mixedAudioBuffer = await createMixedAudioBuffer(audioTracks, videoDurationMs);

    let writeableStream: FileSystemWritableFileStream | null = null;
    let fileHandle: FileSystemFileHandle | null = null;
    try {
        const root = await navigator.storage.getDirectory();
        fileHandle = await root.getFileHandle('audio-sync-utility.mp4', { create: true });
        writeableStream = await fileHandle.createWritable();
    } catch (error) {
        Toast.error('Failed to create the mixed video file. Please try again.');
        throw error;
    }

    const input = new Input({
        source: new BlobSource(videoFile),
        formats: ALL_FORMATS,
    });

    const hasAudioTrack = (await input.getPrimaryAudioTrack()) !== null;
    const output = new Output({
        format: new Mp4OutputFormat(),
        target: new StreamTarget(writeableStream, {
            chunked: true,
        }),
    });
    let conversion: Conversion;
    if (hasAudioTrack) {
        // If video has audio, use Conversion with audio processing
        const sampleRate = mixedAudioBuffer.sampleRate;
        const numberOfChannels = mixedAudioBuffer.numberOfChannels;
        let sampleIndex = 0;

        conversion = await Conversion.init({
            input,
            output,
            video: {}, // Keep video as-is
            audio: {
                codec: 'aac',
                bitrate: 128000,
                sampleRate,
                numberOfChannels,
                forceTranscode: true,
                process: (sample) => {
                    const numFrames = sample.numberOfFrames;
                    const numChannels = sample.numberOfChannels;

                    // Create interleaved f32 data (required format: f32)
                    const dataSize = numFrames * numChannels * 4; // 4 bytes per f32
                    const buffer = new ArrayBuffer(dataSize);
                    const dataView = new Float32Array(buffer);

                    // Get original audio data if video volume > 0
                    let originalData: Float32Array | null = null;
                    if (videoVolume > 0) {
                        const originalSize = sample.allocationSize({
                            planeIndex: 0,
                            format: 'f32',
                        });
                        originalData = new Float32Array(originalSize / 4);
                        sample.copyTo(originalData, { planeIndex: 0, format: 'f32' });
                    }

                    for (let i = 0; i < numFrames; i++) {
                        for (let ch = 0; ch < numChannels; ch++) {
                            const mixedChannel = ch < mixedAudioBuffer.numberOfChannels ? ch : 0;
                            const channelData = mixedAudioBuffer.getChannelData(mixedChannel);
                            const srcIndex = sampleIndex + i;
                            const destIndex = i * numChannels + ch;

                            // Get our mixed audio value
                            let mixedValue = 0;
                            if (srcIndex < channelData.length) {
                                mixedValue = channelData[srcIndex];
                            }

                            // Add original audio if video volume > 0
                            if (videoVolume > 0 && originalData) {
                                const originalValue = originalData[destIndex] ?? 0;
                                // Apply video volume (0-100 -> 0-1)
                                const videoVolumeMultiplier = videoVolume / 100;
                                // Mix: add original (with volume) + our tracks, clamp to prevent clipping
                                dataView[destIndex] = Math.max(
                                    -1,
                                    Math.min(1, originalValue * videoVolumeMultiplier + mixedValue),
                                );
                            } else {
                                // Replace: just use our mixed audio
                                dataView[destIndex] = mixedValue;
                            }
                        }
                    }

                    sampleIndex += numFrames;

                    return new AudioSample({
                        format: 'f32',
                        sampleRate,
                        numberOfChannels: numChannels,
                        timestamp: sample.timestamp,
                        data: buffer,
                    });
                },
            },
        });
    } else {
        // If video has no audio, convert the WAV to get an audio-only MP4,
        // then we need a different strategy. For now, just output video without audio.
        conversion = await Conversion.init({
            input,
            output,
            video: {},
            audio: { discard: true },
        });
    }

    try {
        await conversion.execute();
    } catch (error) {
        Toast.error('Your computer ran out of memory. Please try with a smaller video.');
        throw error;
    } finally {
        input.dispose();
    }

    try {
        return await fileHandle.getFile();
    } catch (error) {
        Toast.error('Failed to get the mixed video file. Please try again.');
        throw error;
    }
}

export function useMediaBunnyMixer(): UseMediaBunnyMixerReturn {
    const [state, setState] = useState<MixerState>({
        videoFile: null,
        videoDurationMs: 0,
        videoPreviewUrl: null,
        audioTracks: [],
        videoVolume: 0,
        isProcessing: false,
        previewUrl: null,
    });

    const previewUrlRef = useRef<string | null>(null);
    const videoPreviewUrlRef = useRef<string | null>(null);

    // Cleanup URLs on unmount
    useEffect(() => {
        return () => {
            if (previewUrlRef.current) {
                URL.revokeObjectURL(previewUrlRef.current);
            }
            if (videoPreviewUrlRef.current) {
                URL.revokeObjectURL(videoPreviewUrlRef.current);
            }
        };
    }, []);

    const setVideoFile = useCallback(async (file: File | null) => {
        // Cleanup old preview URL
        if (videoPreviewUrlRef.current) {
            URL.revokeObjectURL(videoPreviewUrlRef.current);
            videoPreviewUrlRef.current = null;
        }
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }

        if (!file) {
            setState((prev) => ({
                ...prev,
                videoFile: null,
                videoDurationMs: 0,
                videoPreviewUrl: null,
                previewUrl: null,
            }));
            return;
        }

        const durationMs = await getVideoDuration(file);
        const previewUrl = URL.createObjectURL(file);
        videoPreviewUrlRef.current = previewUrl;

        setState((prev) => ({
            ...prev,
            videoFile: file,
            videoDurationMs: durationMs,
            videoPreviewUrl: previewUrl,
            previewUrl: null,
        }));
    }, []);

    const addAudioTrack = useCallback(async (file: File) => {
        const durationMs = await getAudioDuration(file);
        const track: AudioTrack = {
            id: generateId(),
            file,
            name: file.name,
            offsetMs: 0,
            durationMs,
            volume: 100,
        };

        setState((prev) => ({
            ...prev,
            audioTracks: [...prev.audioTracks, track],
        }));
    }, []);

    const removeAudioTrack = useCallback((id: string) => {
        setState((prev) => ({
            ...prev,
            audioTracks: prev.audioTracks.filter((track) => track.id !== id),
        }));
    }, []);

    const updateAudioTrackOffset = useCallback((id: string, offsetMs: number) => {
        setState((prev) => ({
            ...prev,
            audioTracks: prev.audioTracks.map((track) =>
                track.id === id ? { ...track, offsetMs } : track,
            ),
        }));
    }, []);

    const updateAudioTrackVolume = useCallback((id: string, volume: number) => {
        setState((prev) => ({
            ...prev,
            audioTracks: prev.audioTracks.map((track) =>
                track.id === id ? { ...track, volume } : track,
            ),
        }));
    }, []);

    const setVideoVolume = useCallback((volume: number) => {
        setState((prev) => ({
            ...prev,
            videoVolume: volume,
        }));
    }, []);

    const generatePreview = useCallback(async () => {
        if (!state.videoFile || (state.audioTracks.length === 0 && state.videoVolume === 0)) {
            return;
        }

        setState((prev) => ({ ...prev, isProcessing: true }));

        const blob = await mixVideoWithAudio(
            state.videoFile,
            state.audioTracks,
            state.videoVolume,
            state.videoDurationMs,
        );

        // Cleanup old preview URL
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
        }

        const previewUrl = URL.createObjectURL(blob);
        previewUrlRef.current = previewUrl;

        setState((prev) => ({
            ...prev,
            isProcessing: false,
            previewUrl,
        }));
    }, [state.videoFile, state.audioTracks, state.videoVolume, state.videoDurationMs]);

    const downloadVideo = useCallback(async () => {
        if (!state.videoFile) {
            return;
        }
        if (!previewUrlRef.current) {
            return;
        }
        // Trigger download
        const a = document.createElement('a');
        a.href = previewUrlRef.current;
        a.download = `mixed_${state.videoFile.name}`;
        document.body.appendChild(a);
        a.click();
        window.setTimeout(() => {
            document.body.removeChild(a);
        }, 1000);

        setState((prev) => ({
            ...prev,
            isProcessing: false,
        }));
    }, [state.videoFile]);

    const reset = useCallback(() => {
        // Cleanup URLs
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        if (videoPreviewUrlRef.current) {
            URL.revokeObjectURL(videoPreviewUrlRef.current);
            videoPreviewUrlRef.current = null;
        }

        setState({
            videoFile: null,
            videoDurationMs: 0,
            videoPreviewUrl: null,
            audioTracks: [],
            videoVolume: 0,
            isProcessing: false,
            previewUrl: null,
        });
    }, []);

    return {
        state,
        setVideoFile,
        addAudioTrack,
        removeAudioTrack,
        updateAudioTrackOffset,
        updateAudioTrackVolume,
        setVideoVolume,
        generatePreview,
        downloadVideo,
        reset,
    };
}
